/*
 * Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import { data, type ObservableValue } from '@amazon/vinyl-observable'
import { globalRef, logWarn } from '@amazon/vinyl-util'
import { type StreamingQuality } from '@amzn/vinyl-amzn'
import { ctx } from '../env/playbackContext'
import { getPlaybackEnv } from '../env/playbackEnvRef'
import { globalData } from '../util/globalData'

const { target } = ctx('audioQualityEntitlement')

// Account tiers that unlock the HD/Ultra-HD row — mirrors green hornet's
// showHdOption union (Firefly reports the highest tiers separately).
const HD_BENEFIT_QUALITIES = ['HD', 'ULTRA_HD', 'ATMOS', 'SONY_360']

// null until the entitlement query resolves. Consumers treat null as
// "unknown" and fall back to device-only gating, so a slow or failed fetch
// never hides a tier the device already supports.
const playableAudioQualitiesState$ = globalData(() => data<readonly string[] | null>(null))

export const playableAudioQualities$: ObservableValue<readonly string[] | null> =
    playableAudioQualitiesState$

const fetchLatchRef = globalRef(() => ({ started: false }))

/**
 * Fetch the account's entitled audio qualities from Firefly once per
 * session and publish them on {@link playableAudioQualities$}. No-op for
 * signed-out sessions (they have no account entitlement, so gating stays
 * device-only).
 */
export async function loadPlayableAudioQualities(env = getPlaybackEnv()): Promise<void> {
    const latch = fetchLatchRef.value
    if (latch.started) return
    latch.started = true
    const { auth, session } = env
    if (!auth.isSignedIn()) return
    try {
        playableAudioQualitiesState$.value = await session.getPlayableAudioQualities()
    } catch (error) {
        logWarn(target, 'Failed to load playable audio qualities', error)
    }
}

/** True when the account is entitled to the HD/Ultra-HD tier. */
export function accountAllowsHd(qualities: readonly string[]): boolean {
    return HD_BENEFIT_QUALITIES.some((q) => qualities.includes(q))
}

/** True when the account is entitled to the Standard tier. */
export function accountAllowsStandard(qualities: readonly string[]): boolean {
    return qualities.includes('STANDARD')
}

/**
 * Downgrade a persisted tier the account is no longer entitled to,
 * mirroring green hornet's `sanitizePersistedStreamingQuality`
 * (HD → Standard → Data Saver). Returns `undefined` when the current tier
 * is still allowed.
 */
export function downgradeStreamingQuality(
    current: StreamingQuality,
    qualities: readonly string[]
): StreamingQuality | undefined {
    switch (current) {
        case 'HD':
            if (!accountAllowsHd(qualities)) {
                return accountAllowsStandard(qualities) ? 'STANDARD' : 'DATA_SAVER'
            }
            return undefined
        case 'STANDARD':
            return accountAllowsStandard(qualities) ? undefined : 'DATA_SAVER'
        case 'DATA_SAVER':
            return undefined
        default:
            return undefined
    }
}
